import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DollarSign,
  Calculator,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Settings,
  AlertTriangle,
  CheckCircle,
  Percent,
  Coins,
  Edit2,
  Save,
  X,
  Info
} from 'lucide-react'
import { adminApi } from '@/lib/api'
import { cn } from '@/lib/utils'

interface PricingSettings {
  marginPercent: number
  creditsPerDollar: number
  appStoreFeePercent: number
}

interface PeriodEconomics {
  price: number
  maxProviderCost: number
  appStoreFee: number
  netRevenue: number
  guaranteedProfit: number
  guaranteedMarginPercent: number
  expectedProviderCost: number
  expectedProfit: number
  expectedMarginPercent: number
  breakEvenCredits: number
  breakEvenPrice: number
}

interface TierWithCostAnalysis {
  id: string
  tierName: string
  displayName: string
  monthlyCredits: number
  pricing: {
    monthly: { price: number }
    yearly?: { price: number }
  }
  economics: {
    monthly: PeriodEconomics
    yearly?: PeriodEconomics
  }
}

interface TierCostAnalysisResponse {
  settings: PricingSettings
  avgUtilization: number
  tiers: TierWithCostAnalysis[]
}

interface TierEditState {
  monthlyCredits: number
  monthlyPrice: number
  yearlyPrice: number | null
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`
}

export const PricingPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<PricingSettings | null>(null)
  const [tierAnalysis, setTierAnalysis] = useState<TierCostAnalysisResponse | null>(null)
  const [avgUtilization, setAvgUtilization] = useState(0.6)
  const [editedSettings, setEditedSettings] = useState<Partial<PricingSettings>>({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Tier editing state
  const [editingTierId, setEditingTierId] = useState<string | null>(null)
  const [tierEditState, setTierEditState] = useState<TierEditState | null>(null)
  const [savingTier, setSavingTier] = useState(false)

  const loadData = async () => {
    try {
      setLoading(true)
      const [settingsData, analysisData] = await Promise.all([
        adminApi.getPricingSettings(),
        adminApi.getTierCostAnalysis(avgUtilization)
      ])
      setSettings(settingsData)
      setTierAnalysis(analysisData)
      setEditedSettings({})
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Failed to load pricing data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    // Reload tier analysis when utilization changes
    const loadAnalysis = async () => {
      try {
        const analysisData = await adminApi.getTierCostAnalysis(avgUtilization)
        setTierAnalysis(analysisData)
      } catch (error) {
        console.error('Failed to load tier analysis:', error)
      }
    }
    if (!loading) {
      loadAnalysis()
    }
  }, [avgUtilization])

  const handleSettingChange = (key: keyof PricingSettings, value: number) => {
    setEditedSettings(prev => ({ ...prev, [key]: value }))
    setHasUnsavedChanges(true)
  }

  const saveSettings = async () => {
    if (!hasUnsavedChanges) return

    try {
      setSaving(true)
      await adminApi.updatePricingSettings(editedSettings)
      await loadData()
    } catch (error) {
      console.error('Failed to save settings:', error)
    } finally {
      setSaving(false)
    }
  }

  const getCurrentValue = (key: keyof PricingSettings): number => {
    if (editedSettings[key] !== undefined) return editedSettings[key]!
    if (settings?.[key] !== undefined) return settings[key]
    return 0
  }

  // Start editing a tier
  const startEditingTier = (tier: TierWithCostAnalysis) => {
    setEditingTierId(tier.id)
    setTierEditState({
      monthlyCredits: tier.monthlyCredits,
      monthlyPrice: tier.pricing.monthly.price,
      yearlyPrice: tier.pricing.yearly?.price ?? null
    })
  }

  // Cancel tier editing
  const cancelEditingTier = () => {
    setEditingTierId(null)
    setTierEditState(null)
  }

  // Save tier changes
  const saveTierChanges = async (tierId: string) => {
    if (!tierEditState) return

    try {
      setSavingTier(true)
      await adminApi.updateTierPricing(tierId, {
        monthlyCredits: tierEditState.monthlyCredits,
        monthlyPrice: tierEditState.monthlyPrice,
        yearlyPrice: tierEditState.yearlyPrice ?? undefined
      })
      setEditingTierId(null)
      setTierEditState(null)
      // Reload tier analysis
      const analysisData = await adminApi.getTierCostAnalysis(avgUtilization)
      setTierAnalysis(analysisData)
    } catch (error) {
      console.error('Failed to save tier:', error)
    } finally {
      setSavingTier(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Calculate effective multiplier for formula display
  const marginMultiplier = 1 + (getCurrentValue('marginPercent') / 100)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pricing Configuration</h1>
          <p className="text-muted-foreground">
            Configure credit pricing, margins, and tier economics
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="settings">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="settings">Pricing Settings</TabsTrigger>
          <TabsTrigger value="tiers">Tier Economics</TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Settings className="h-5 w-5 text-blue-600" />
                <CardTitle>Global Pricing Settings</CardTitle>
              </div>
              <CardDescription>
                Configure the global parameters used for credit calculations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Margin Percent */}
              <div className="space-y-2">
                <Label htmlFor="marginPercent" className="flex items-center gap-2">
                  <Percent className="h-4 w-4 text-green-600" />
                  Margin Percentage
                </Label>
                <div className="text-sm text-muted-foreground mb-2">
                  Markup percentage applied to provider costs. This determines how many credits
                  are charged relative to the actual cost. (e.g., 30% means charging 1.3x the provider cost)
                </div>
                <div className="flex items-center space-x-2">
                  <Input
                    id="marginPercent"
                    type="number"
                    step="1"
                    min="0"
                    max="200"
                    value={getCurrentValue('marginPercent')}
                    onChange={(e) => handleSettingChange('marginPercent', parseFloat(e.target.value))}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <span className="text-sm text-muted-foreground ml-4">
                    (multiplier: {marginMultiplier.toFixed(2)}x)
                  </span>
                </div>
              </div>

              {/* Credits Per Dollar */}
              <div className="space-y-2">
                <Label htmlFor="creditsPerDollar" className="flex items-center gap-2">
                  <Coins className="h-4 w-4 text-yellow-600" />
                  Credits Per Dollar
                </Label>
                <div className="text-sm text-muted-foreground mb-2">
                  Number of credits that represent $1 USD of provider cost.
                  Higher values mean finer granularity in credit deductions.
                </div>
                <div className="flex items-center space-x-2">
                  <Input
                    id="creditsPerDollar"
                    type="number"
                    step="100"
                    min="100"
                    max="100000"
                    value={getCurrentValue('creditsPerDollar')}
                    onChange={(e) => handleSettingChange('creditsPerDollar', parseFloat(e.target.value))}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">credits = $1 USD</span>
                </div>
              </div>

              {/* App Store Fee */}
              <div className="space-y-2">
                <Label htmlFor="appStoreFeePercent" className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-purple-600" />
                  App Store Fee (Profit Analysis Only)
                </Label>
                <div className="text-sm text-muted-foreground mb-2 flex items-start gap-2">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
                  <span>
                    This is used <strong>only for profit analysis</strong> in the Tier Economics tab.
                    It does NOT affect credit deduction calculations. The app store fee is deducted from
                    subscription revenue to show true profitability. (e.g., 15% for Small Business Program, 30% standard)
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Input
                    id="appStoreFeePercent"
                    type="number"
                    step="1"
                    min="0"
                    max="50"
                    value={getCurrentValue('appStoreFeePercent')}
                    onChange={(e) => handleSettingChange('appStoreFeePercent', parseFloat(e.target.value))}
                    className="w-32"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  {hasUnsavedChanges && (
                    <p className="text-sm text-amber-600">
                      You have unsaved changes
                    </p>
                  )}
                </div>
                <Button
                  onClick={saveSettings}
                  disabled={saving || !hasUnsavedChanges}
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Formula Explanation */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Calculator className="h-5 w-5 text-orange-600" />
                <CardTitle>Credit Calculation Formula</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-muted p-4 rounded-lg font-mono text-sm">
                <p className="text-muted-foreground mb-2">Credits deducted per request:</p>
                <p className="text-lg">
                  creditsDeducted = providerCost × (1 + marginPercent/100) × creditsPerDollar
                </p>
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                <p>Example with current settings:</p>
                <p className="mt-2">
                  If a request costs ${(0.001).toFixed(4)} from the provider:
                </p>
                <p className="font-mono mt-1">
                  credits = $0.001 × {marginMultiplier.toFixed(2)} × {getCurrentValue('creditsPerDollar')} = {(0.001 * marginMultiplier * getCurrentValue('creditsPerDollar')).toFixed(2)} credits
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tier Economics Tab */}
        <TabsContent value="tiers" className="space-y-6">
          {/* Utilization Selector */}
          <Card>
            <CardHeader>
              <CardTitle>Utilization Scenario</CardTitle>
              <CardDescription>
                Adjust the expected credit utilization rate to see how it affects profitability
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Label>Average Utilization:</Label>
                <Select
                  value={avgUtilization.toString()}
                  onValueChange={(v) => setAvgUtilization(parseFloat(v))}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.3">30% (Low)</SelectItem>
                    <SelectItem value="0.5">50% (Medium)</SelectItem>
                    <SelectItem value="0.6">60% (Default)</SelectItem>
                    <SelectItem value="0.8">80% (High)</SelectItem>
                    <SelectItem value="1">100% (Full)</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">
                  of monthly credits used on average
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Tier Analysis Table */}
          {tierAnalysis?.tiers.map((tier) => {
            const isEditing = editingTierId === tier.id

            return (
              <Card key={tier.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-5 w-5 text-green-600" />
                      <CardTitle>{tier.displayName}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelEditingTier}
                            disabled={savingTier}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => saveTierChanges(tier.id)}
                            disabled={savingTier}
                          >
                            {savingTier ? (
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4 mr-1" />
                            )}
                            Save
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEditingTier(tier)}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Editable tier configuration */}
                  {isEditing && tierEditState ? (
                    <div className="mb-6 p-4 bg-muted rounded-lg">
                      <h4 className="font-medium mb-4">Edit Tier Configuration</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`credits-${tier.id}`}>Monthly Credits</Label>
                          <Input
                            id={`credits-${tier.id}`}
                            type="number"
                            value={tierEditState.monthlyCredits}
                            onChange={(e) => setTierEditState({
                              ...tierEditState,
                              monthlyCredits: parseInt(e.target.value) || 0
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`monthly-${tier.id}`}>Monthly Price ($)</Label>
                          <Input
                            id={`monthly-${tier.id}`}
                            type="number"
                            step="0.01"
                            value={tierEditState.monthlyPrice}
                            onChange={(e) => setTierEditState({
                              ...tierEditState,
                              monthlyPrice: parseFloat(e.target.value) || 0
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`yearly-${tier.id}`}>Yearly Price ($)</Label>
                          <Input
                            id={`yearly-${tier.id}`}
                            type="number"
                            step="0.01"
                            value={tierEditState.yearlyPrice ?? ''}
                            placeholder="Optional"
                            onChange={(e) => setTierEditState({
                              ...tierEditState,
                              yearlyPrice: e.target.value ? parseFloat(e.target.value) : null
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4 flex items-center gap-4">
                      <Badge variant="outline">
                        {tier.monthlyCredits.toLocaleString()} credits/month
                      </Badge>
                      <Badge variant="secondary">
                        Monthly: {formatCurrency(tier.pricing.monthly.price)}
                      </Badge>
                      {tier.pricing.yearly && (
                        <Badge variant="secondary">
                          Yearly: {formatCurrency(tier.pricing.yearly.price)}
                        </Badge>
                      )}
                    </div>
                  )}

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">App Store Fee</TableHead>
                        <TableHead className="text-right">Net Revenue</TableHead>
                        <TableHead className="text-right">Max Provider Cost</TableHead>
                        <TableHead className="text-right">Expected Cost</TableHead>
                        <TableHead className="text-right">Expected Profit</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Monthly */}
                      <TableRow>
                        <TableCell className="font-medium">Monthly</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(tier.pricing.monthly.price)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(tier.economics.monthly.appStoreFee)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(tier.economics.monthly.netRevenue)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatCurrency(tier.economics.monthly.maxProviderCost)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(tier.economics.monthly.expectedProviderCost)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={cn(
                            "font-medium",
                            tier.economics.monthly.expectedProfit >= 0 ? "text-green-600" : "text-red-600"
                          )}>
                            {formatCurrency(tier.economics.monthly.expectedProfit)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={tier.economics.monthly.expectedMarginPercent >= 0 ? "default" : "destructive"}>
                            {tier.economics.monthly.expectedMarginPercent >= 0 ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <AlertTriangle className="h-3 w-3 mr-1" />
                            )}
                            {formatPercent(tier.economics.monthly.expectedMarginPercent)}
                          </Badge>
                        </TableCell>
                      </TableRow>

                      {/* Yearly */}
                      {tier.pricing.yearly && tier.economics.yearly && (
                        <TableRow>
                          <TableCell className="font-medium">Yearly</TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(tier.pricing.yearly.price)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(tier.economics.yearly.appStoreFee)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(tier.economics.yearly.netRevenue)}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {formatCurrency(tier.economics.yearly.maxProviderCost)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(tier.economics.yearly.expectedProviderCost)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={cn(
                              "font-medium",
                              tier.economics.yearly.expectedProfit >= 0 ? "text-green-600" : "text-red-600"
                            )}>
                              {formatCurrency(tier.economics.yearly.expectedProfit)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant={tier.economics.yearly.expectedMarginPercent >= 0 ? "default" : "destructive"}>
                              {tier.economics.yearly.expectedMarginPercent >= 0 ? (
                                <CheckCircle className="h-3 w-3 mr-1" />
                              ) : (
                                <AlertTriangle className="h-3 w-3 mr-1" />
                              )}
                              {formatPercent(tier.economics.yearly.expectedMarginPercent)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  {/* Break-even Analysis */}
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-medium mb-2">Break-even Analysis (Monthly)</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Break-even credits:</span>
                        <span className="ml-2 font-mono">
                          {tier.economics.monthly.breakEvenCredits.toLocaleString()}
                        </span>
                        <span className="ml-1 text-muted-foreground">
                          ({formatPercent((tier.economics.monthly.breakEvenCredits / tier.monthlyCredits) * 100)} utilization)
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Break-even price:</span>
                        <span className="ml-2 font-mono">
                          {formatCurrency(tier.economics.monthly.breakEvenPrice)}
                        </span>
                        {tier.pricing.monthly.price > tier.economics.monthly.breakEvenPrice ? (
                          <Badge variant="default" className="ml-2">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Profitable
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="ml-2">
                            <TrendingDown className="h-3 w-3 mr-1" />
                            Below Cost
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default PricingPage
